import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { fmtUGX, fmtNum, shortDateTime, shortDate } from "@/lib/format";
import { useMemo, useState } from "react";
import {
  TrendingUp, TrendingDown, Activity, DollarSign, Receipt, Users, GraduationCap,
  Package, AlertTriangle, ArrowUpRight, ArrowDownLeft, FileText, ShieldAlert,
  Sparkles, Plus, ChevronRight, Building2, Banknote, Clock4, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";

/* ============================================================================
   EXECUTIVE PANEL — fintech-grade revenue command center
============================================================================ */
export function ExecutivePanel({ merchantId, feeBps }: { merchantId: string; feeBps: number }) {
  const { data: sales = [] } = useQuery({
    queryKey: ["exec-sales", merchantId],
    refetchInterval: 8000,
    queryFn: async () => (await supabase.from("pos_sales").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(500)).data ?? [],
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["exec-settle", merchantId],
    queryFn: async () => (await supabase.from("settlements").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(60)).data ?? [],
  });
  const { data: txns = [] } = useQuery({
    queryKey: ["exec-txns", merchantId],
    queryFn: async () => (await supabase.from("transactions").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(500)).data ?? [],
  });

  const now = Date.now();
  const buckets = (ms: number) => sales.filter((s: any) => now - new Date(s.created_at).getTime() < ms);
  const sum = (rows: any[]) => rows.reduce((a, s) => a + (s.total_ugx ?? 0), 0);
  const today = buckets(86_400_000); const week = buckets(7 * 86_400_000); const month = buckets(30 * 86_400_000); const year = buckets(365 * 86_400_000);
  const gross = sum(year);
  const commission = txns.reduce((a, t: any) => a + (t.fee_ugx ?? 0), 0);
  const net = gross - commission;
  const aov = today.length ? sum(today) / today.length : 0;
  const settled = settlements.reduce((a, s: any) => a + (s.gross_ugx ?? 0), 0);
  const outstanding = Math.max(0, gross - settled);

  // 14-day spark
  const days = useMemo(() => {
    const arr: { d: string; total: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const start = now - (i + 1) * 86_400_000; const end = now - i * 86_400_000;
      const v = sales.filter((s: any) => { const t = new Date(s.created_at).getTime(); return t >= start && t < end; }).reduce((a: number, s: any) => a + (s.total_ugx ?? 0), 0);
      arr.push({ d: new Date(start).toLocaleDateString("en-UG", { weekday: "short" }), total: v });
    }
    return arr;
  }, [sales]);
  const peak = Math.max(1, ...days.map(d => d.total));

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi tone="emerald" icon={DollarSign} label="Today" value={fmtUGX(sum(today))} hint={`${today.length} sales · AOV ${fmtUGX(aov)}`} />
        <Kpi icon={TrendingUp} label="This week" value={fmtUGX(sum(week))} hint={`${week.length} sales`} />
        <Kpi icon={Activity} label="This month" value={fmtUGX(sum(month))} hint={`${month.length} sales`} />
        <Kpi tone="amber" icon={Receipt} label="Annual gross" value={fmtUGX(gross)} hint={`${year.length} sales`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Net revenue (annual)</div>
          <div className="mt-2 font-display text-3xl">{fmtUGX(net)}</div>
          <div className="mt-1 text-xs text-muted-foreground">Gross {fmtUGX(gross)} − fees {fmtUGX(commission)}</div>
          <div className="mt-4 flex justify-between text-xs">
            <span className="text-muted-foreground">Platform commission</span>
            <span className="font-mono">{(feeBps / 100).toFixed(2)}%</span>
          </div>
          <Progress value={Math.min(100, (commission / Math.max(1, gross)) * 100)} className="mt-1 h-1.5" />
        </Card>

        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Settlement balance</div>
          <div className="mt-2 font-display text-3xl">{fmtUGX(settled)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{settlements.length} batches landed</div>
          <div className="mt-3 rounded-lg bg-amber-500/10 p-2 text-xs">
            <span className="font-semibold text-amber-700 dark:text-amber-400">Outstanding</span>{" "}
            <span className="font-mono">{fmtUGX(outstanding)}</span> awaiting next batch
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">14-day revenue trend</div>
          <div className="mt-3 flex h-24 items-end gap-1">
            {days.map((d, i) => (
              <div key={i} className="group flex flex-1 flex-col items-center justify-end">
                <div className="w-full rounded-t bg-primary/70 transition group-hover:bg-primary" style={{ height: `${(d.total / peak) * 100}%` }} title={`${d.d}: ${fmtUGX(d.total)}`} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
            <span>{days[0]?.d}</span><span>peak {fmtUGX(peak)}</span><span>{days.at(-1)?.d}</span>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg">Live transaction feed</div>
          <Badge variant="outline" className="text-[10px]">refreshing</Badge>
        </div>
        <div className="mt-3 max-h-[260px] divide-y overflow-auto">
          {sales.slice(0, 30).map((s: any) => (
            <div key={s.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <div className="font-medium">POS · {(s.items as any[])?.length ?? 0} items</div>
                <div className="text-xs text-muted-foreground">{shortDateTime(s.created_at)}</div>
              </div>
              <div className="font-mono">{fmtUGX(s.total_ugx)}</div>
            </div>
          ))}
          {sales.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No transactions yet. Open a shift to start.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================================
   INVENTORY INTELLIGENCE — fast movers, dead stock, reorder
============================================================================ */
export function InventoryIntelligencePanel({ merchantId }: { merchantId: string }) {
  const { data: stock = [] } = useQuery({
    queryKey: ["inv-stock", merchantId],
    queryFn: async () => (await supabase.from("stock_items").select("*").eq("merchant_id", merchantId)).data ?? [],
  });
  const { data: sales = [] } = useQuery({
    queryKey: ["inv-sales", merchantId],
    queryFn: async () => (await supabase.from("pos_sales").select("items, created_at").eq("merchant_id", merchantId).gte("created_at", new Date(Date.now() - 30 * 86_400_000).toISOString())).data ?? [],
  });

  const stats = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    for (const s of sales) for (const it of (s.items as any[]) ?? []) {
      const cur = map.get(it.id) ?? { name: it.name, qty: 0, revenue: 0 };
      cur.qty += it.qty; cur.revenue += it.qty * it.price_ugx; map.set(it.id, cur);
    }
    const fast = Array.from(map.entries()).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.qty - a.qty).slice(0, 6);
    const dead = stock.filter((s: any) => !map.has(s.id));
    const reorder = stock.filter((s: any) => s.quantity < 10);
    return { fast, dead, reorder };
  }, [sales, stock]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 font-display text-base"><TrendingUp className="h-4 w-4 text-emerald-600" />Fast movers (30d)</div>
          <div className="mt-3 space-y-2">
            {stats.fast.map((f, i) => (
              <div key={f.id} className="rounded-lg border bg-card/40 p-2 text-sm">
                <div className="flex justify-between">
                  <span><span className="text-muted-foreground mr-1">#{i + 1}</span>{f.name}</span>
                  <span className="font-mono">{fmtNum(f.qty)}</span>
                </div>
                <Progress value={Math.min(100, (f.qty / (stats.fast[0]?.qty || 1)) * 100)} className="mt-1.5 h-1" />
              </div>
            ))}
            {stats.fast.length === 0 && <div className="text-xs text-muted-foreground">No sales in last 30 days.</div>}
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 font-display text-base"><TrendingDown className="h-4 w-4 text-rose-600" />Dead stock</div>
          <p className="mt-1 text-xs text-muted-foreground">No sales in 30 days. Consider promo or transfer.</p>
          <div className="mt-3 space-y-1.5">
            {stats.dead.slice(0, 8).map((s: any) => (
              <div key={s.id} className="flex justify-between rounded-lg bg-muted/40 px-3 py-1.5 text-sm">
                <span>{s.name}</span><span className="text-xs text-muted-foreground">{s.quantity} on hand</span>
              </div>
            ))}
            {stats.dead.length === 0 && <div className="text-xs text-muted-foreground">All stock is moving — great signal.</div>}
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 font-display text-base"><AlertTriangle className="h-4 w-4 text-amber-600" />Reorder now</div>
          <p className="mt-1 text-xs text-muted-foreground">Stock under 10 units.</p>
          <div className="mt-3 space-y-1.5">
            {stats.reorder.map((s: any) => (
              <div key={s.id} className="flex justify-between rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-1.5 text-sm">
                <span>{s.name}</span><Badge variant="destructive" className="text-[10px]">{s.quantity} left</Badge>
              </div>
            ))}
            {stats.reorder.length === 0 && <div className="text-xs text-muted-foreground">All items above reorder threshold.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================================
   BULK ORDERS — school institutional procurement
============================================================================ */
export function BulkOrdersPanel({ merchantId }: { merchantId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [school, setSchool] = useState("");
  const [items, setItems] = useState("Uniform sweater (M) x 80 @ 35000\nMaths textbooks x 120 @ 18000");
  const [quote, setQuote] = useState("4960000");
  const [notes, setNotes] = useState("Term opening delivery — Mon 7AM");

  const { data: schools = [] } = useQuery({
    queryKey: ["all-schools-bulk"],
    queryFn: async () => (await (supabase.from as any)("schools_public").select("id, name, district")).data ?? [],
  });
  const { data: orders = [] } = useQuery({
    queryKey: ["bulk-orders", merchantId],
    refetchInterval: 6000,
    queryFn: async () => (await (supabase.from as any)("bulk_orders").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false })).data ?? [],
  });

  const create = useMutation({
    mutationFn: async () => {
      const sid = school || schools[0]?.id;
      const { error } = await (supabase.from as any)("bulk_orders").insert({
        merchant_id: merchantId, school_id: sid, requested_by: user!.id,
        items: items.split("\n").filter(Boolean).map(l => ({ line: l })),
        quote_ugx: Number(quote) || 0, notes,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Quotation submitted"); setOpen(false); qc.invalidateQueries({ queryKey: ["bulk-orders", merchantId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const advance = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: string }) => {
      const patch: any = { status: next };
      if (next === "approved") patch.approved_at = new Date().toISOString();
      if (next === "delivered") patch.delivered_at = new Date().toISOString();
      if (next === "invoiced") { patch.invoiced_at = new Date().toISOString(); patch.invoice_no = `INV-${Date.now().toString().slice(-6)}`; }
      const { error } = await (supabase.from as any)("bulk_orders").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bulk-orders", merchantId] }),
  });

  const cols = ["quotation", "approved", "po_issued", "in_delivery", "delivered", "invoiced", "paid"] as const;
  const nextFor: Record<string, string> = { quotation: "approved", approved: "po_issued", po_issued: "in_delivery", in_delivery: "delivered", delivered: "invoiced", invoiced: "paid" };

  return (
    <Card>
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <div className="font-display text-lg flex items-center gap-2"><Building2 className="h-4 w-4" />School bulk orders</div>
          <p className="text-xs text-muted-foreground">Institutional procurement: quotation → approval → PO → delivery → invoice</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="mr-1 h-3 w-3" />New quotation</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit quotation to a school</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>School</Label>
                <Select value={school} onValueChange={setSchool}>
                  <SelectTrigger><SelectValue placeholder={schools[0]?.name ?? "Pick school"} /></SelectTrigger>
                  <SelectContent>{schools.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.district ?? "—"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Line items (one per line)</Label><Textarea rows={4} value={items} onChange={e => setItems(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Quote (UGX)</Label><Input type="number" value={quote} onChange={e => setQuote(e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Delivery window</Label><Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
              </div>
              <DialogFooter><Button onClick={() => create.mutate()} disabled={create.isPending}>Send to school</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto p-3">
        <div className="flex min-w-[1000px] gap-3">
          {cols.map(c => {
            const rows = orders.filter((o: any) => o.status === c);
            const subtotal = rows.reduce((a: number, o: any) => a + (o.quote_ugx ?? 0), 0);
            return (
              <div key={c} className="w-[160px] flex-shrink-0">
                <div className="mb-2 flex items-center justify-between px-1 text-xs">
                  <span className="font-semibold capitalize">{c.replace("_", " ")}</span>
                  <Badge variant="outline" className="h-4 text-[9px]">{rows.length}</Badge>
                </div>
                <div className="space-y-2">
                  {rows.map((o: any) => (
                    <div key={o.id} className="rounded-lg border bg-card p-2 text-xs shadow-sm">
                      <div className="font-mono text-[10px] text-muted-foreground">{o.id.slice(0, 6).toUpperCase()}</div>
                      <div className="mt-0.5 font-semibold">{fmtUGX(o.quote_ugx)}</div>
                      <div className="text-[10px] text-muted-foreground">{(o.items as any[])?.length ?? 0} lines</div>
                      {nextFor[c] && <Button size="sm" variant="outline" className="mt-1.5 h-6 w-full text-[10px]" onClick={() => advance.mutate({ id: o.id, next: nextFor[c] })}>→ {nextFor[c].replace("_", " ")}</Button>}
                    </div>
                  ))}
                  {rows.length === 0 && <div className="rounded-lg bg-muted/30 p-3 text-center text-[10px] text-muted-foreground">—</div>}
                </div>
                {subtotal > 0 && <div className="mt-2 px-1 text-[10px] text-muted-foreground">{fmtUGX(subtotal)}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ============================================================================
   REFUNDS & DISPUTES
============================================================================ */
export function RefundsPanel({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: ["refunds", merchantId],
    refetchInterval: 8000,
    queryFn: async () => (await (supabase.from as any)("refund_requests").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false })).data ?? [],
  });

  const resolve = useMutation({
    mutationFn: async ({ id, status, note }: { id: string; status: string; note?: string }) => {
      const { error } = await (supabase.from as any)("refund_requests").update({ status, resolved_at: new Date().toISOString(), resolution_note: note ?? null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["refunds", merchantId] }); },
  });

  const open = rows.filter((r: any) => ["open", "investigating"].includes(r.status));
  const closed = rows.filter((r: any) => !["open", "investigating"].includes(r.status));

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <div className="border-b p-3 font-display text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-600" />Open · investigating <Badge variant="outline" className="text-[10px]">{open.length}</Badge>
        </div>
        <div className="divide-y max-h-[480px] overflow-auto">
          {open.map((r: any) => (
            <div key={r.id} className="p-3 text-sm">
              <div className="flex justify-between">
                <span className="font-medium">{r.reason}</span>
                <span className="font-mono">{fmtUGX(r.amount_ugx)}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{r.requester_role} · {r.category} · {shortDateTime(r.created_at)}</div>
              <div className="mt-2 flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => resolve.mutate({ id: r.id, status: "investigating" })}>Investigate</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-600" onClick={() => resolve.mutate({ id: r.id, status: "refunded", note: "Wallet credited" })}>Refund</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600" onClick={() => resolve.mutate({ id: r.id, status: "rejected", note: "Out of policy" })}>Reject</Button>
              </div>
            </div>
          ))}
          {open.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No open disputes. 👌</div>}
        </div>
      </Card>

      <Card>
        <div className="border-b p-3 font-display text-base">Recently resolved</div>
        <div className="divide-y max-h-[480px] overflow-auto">
          {closed.slice(0, 30).map((r: any) => (
            <div key={r.id} className="flex justify-between p-3 text-sm">
              <div>
                <div className="font-medium">{r.reason}</div>
                <div className="text-[11px] text-muted-foreground">{r.resolution_note ?? r.status}</div>
              </div>
              <div className="text-right">
                <div className="font-mono">{fmtUGX(r.amount_ugx)}</div>
                <Badge variant={r.status === "refunded" ? "secondary" : "outline"} className="text-[10px]">{r.status}</Badge>
              </div>
            </div>
          ))}
          {closed.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">Nothing in history yet.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================================
   CRM — customers (parents, students, schools)
============================================================================ */
export function CRMPanel({ merchantId }: { merchantId: string }) {
  const { data: sales = [] } = useQuery({
    queryKey: ["crm-sales", merchantId],
    queryFn: async () => (await supabase.from("pos_sales").select("wallet_id, total_ugx, created_at").eq("merchant_id", merchantId)).data ?? [],
  });
  const { data: orders = [] } = useQuery({
    queryKey: ["crm-orders", merchantId],
    queryFn: async () => (await supabase.from("merchant_orders").select("guardian_id, school_id, total_ugx, created_at").eq("merchant_id", merchantId)).data ?? [],
  });

  const top = useMemo(() => {
    const m = new Map<string, { id: string; spend: number; visits: number }>();
    for (const s of sales) {
      const cur = m.get(s.wallet_id) ?? { id: s.wallet_id, spend: 0, visits: 0 };
      cur.spend += s.total_ugx; cur.visits += 1; m.set(s.wallet_id, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.spend - a.spend).slice(0, 10);
  }, [sales]);

  const schools = useMemo(() => {
    const m = new Map<string, { id: string; revenue: number; orders: number }>();
    for (const o of orders) if (o.school_id) {
      const cur = m.get(o.school_id) ?? { id: o.school_id, revenue: 0, orders: 0 };
      cur.revenue += o.total_ugx; cur.orders += 1; m.set(o.school_id, cur);
    }
    return Array.from(m.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 6);
  }, [orders]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <div className="border-b p-3 font-display text-base flex items-center gap-2"><Users className="h-4 w-4" />Top student wallets</div>
        <div className="divide-y">
          {top.map((t, i) => (
            <div key={t.id} className="flex items-center justify-between p-3 text-sm">
              <div>
                <div className="font-medium">Wallet #{i + 1}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{t.id.slice(0, 12)}…</div>
              </div>
              <div className="text-right">
                <div className="font-mono">{fmtUGX(t.spend)}</div>
                <div className="text-[10px] text-muted-foreground">{t.visits} visits</div>
              </div>
            </div>
          ))}
          {top.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No repeat customers yet.</div>}
        </div>
      </Card>

      <Card>
        <div className="border-b p-3 font-display text-base flex items-center gap-2"><GraduationCap className="h-4 w-4" />Schools served</div>
        <div className="divide-y">
          {schools.map(s => (
            <div key={s.id} className="flex items-center justify-between p-3 text-sm">
              <div className="font-mono text-[11px] text-muted-foreground">{s.id.slice(0, 10)}…</div>
              <div className="text-right">
                <div className="font-mono">{fmtUGX(s.revenue)}</div>
                <div className="text-[10px] text-muted-foreground">{s.orders} orders</div>
              </div>
            </div>
          ))}
          {schools.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No school orders yet. Submit a quotation in Bulk Orders.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================================
   FINANCE — statements, VAT, settlement queue
============================================================================ */
export function FinancePanel({ merchantId }: { merchantId: string; feeBps?: number }) {
  const { data: sales = [] } = useQuery({
    queryKey: ["fin-sales", merchantId],
    queryFn: async () => (await supabase.from("pos_sales").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(1000)).data ?? [],
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["fin-settle", merchantId],
    queryFn: async () => (await supabase.from("settlements").select("*").eq("merchant_id", merchantId).order("created_at", { ascending: false }).limit(60)).data ?? [],
  });

  const groupBy = (mode: "day" | "week" | "month") => {
    const map = new Map<string, { period: string; revenue: number; count: number }>();
    for (const s of sales) {
      const d = new Date(s.created_at);
      const key = mode === "day" ? d.toISOString().slice(0, 10)
        : mode === "week" ? `${d.getFullYear()}-W${Math.ceil((d.getDate() + 6) / 7)}`
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const cur = map.get(key) ?? { period: key, revenue: 0, count: 0 };
      cur.revenue += s.total_ugx; cur.count += 1; map.set(key, cur);
    }
    return Array.from(map.values()).slice(0, 14);
  };

  const [mode, setMode] = useState<"day" | "week" | "month">("day");
  const rows = groupBy(mode);
  const vat = rows.reduce((a, r) => a + Math.round(r.revenue * 0.18), 0);

  const exportCsv = () => {
    const header = "period,revenue,count,vat_18pct\n";
    const body = rows.map(r => `${r.period},${r.revenue},${r.count},${Math.round(r.revenue * 0.18)}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `statement-${mode}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Statements</div>
            <p className="text-xs text-muted-foreground">Drill into revenue by period. VAT estimated at 18% (URA standard).</p>
          </div>
          <div className="flex gap-1">
            {(["day", "week", "month"] as const).map(m => (
              <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} className="h-7 capitalize" onClick={() => setMode(m)}>{m}ly</Button>
            ))}
            <Button size="sm" variant="outline" className="h-7" onClick={exportCsv}><FileText className="mr-1 h-3 w-3" />CSV</Button>
          </div>
        </div>
        <div className="mt-4 max-h-[280px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="p-2 text-left">Period</th><th className="p-2 text-right">Sales</th><th className="p-2 text-right">Revenue</th><th className="p-2 text-right">Est. VAT</th></tr>
            </thead>
            <tbody className="divide-y">
              {rows.map(r => (
                <tr key={r.period}>
                  <td className="p-2 font-mono text-xs">{r.period}</td>
                  <td className="p-2 text-right">{fmtNum(r.count)}</td>
                  <td className="p-2 text-right font-mono">{fmtUGX(r.revenue)}</td>
                  <td className="p-2 text-right font-mono text-muted-foreground">{fmtUGX(Math.round(r.revenue * 0.18))}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-xs text-muted-foreground">No statements yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">Estimated VAT across visible rows: <span className="font-mono">{fmtUGX(vat)}</span></div>
      </Card>

      <Card>
        <div className="border-b p-3 font-display text-base flex items-center gap-2"><Banknote className="h-4 w-4" />Settlement queue</div>
        <div className="divide-y">
          {settlements.map((s: any) => (
            <div key={s.id} className="flex items-center justify-between p-3 text-sm">
              <div>
                <div className="font-medium">{s.reference ?? s.id.slice(0, 8)}</div>
                <div className="text-[11px] text-muted-foreground">{shortDate(s.batch_date)} · {s.tx_count} tx</div>
              </div>
              <div className="text-right">
                <div className="font-mono">{fmtUGX(s.net_ugx)}</div>
                <Badge variant={s.status === "completed" ? "secondary" : s.status === "failed" ? "destructive" : "outline"} className="text-[10px]">{s.status}</Badge>
              </div>
            </div>
          ))}
          {settlements.length === 0 && <div className="p-6 text-center text-xs text-muted-foreground">No batches yet. Daily settlements queue end-of-day.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================================
   Shared UI
============================================================================ */
function Kpi({ icon: Icon, label, value, hint, tone }: { icon: any; label: string; value: string; hint?: string; tone?: "emerald" | "amber" | "rose" }) {
  const ring = tone === "emerald" ? "ring-emerald-500/30" : tone === "amber" ? "ring-amber-500/30" : tone === "rose" ? "ring-rose-500/30" : "ring-primary/20";
  return (
    <Card className={`relative overflow-hidden p-4 ring-1 ${ring}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
        {label} <Icon className="h-3.5 w-3.5 opacity-60" />
      </div>
      <div className="mt-1 font-display text-2xl">{value}</div>
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </Card>
  );
}
